import { apiClient } from './client';
import type { PaginatedResponse } from '../types/api';
import type {
  ModuleQueueRow,
  ModuleStats,
  ProductionActivity,
  ProductionModuleKey,
  ScrapReport,
  ScrapReportCreatePayload,
  ShiftReport,
  ShiftReportCreatePayload,
} from '../types/production';

const BASE = '/production/';

interface ShiftReportListParams {
  module: ProductionModuleKey;
  page?: number;
  page_size?: number;
  ordering?: string;
}

export const productionApi = {
  /**
   * Черга цеху. Пагінації немає свідомо — черга коротка (десятки рядків),
   * а розбиття на сторінки ламало б підсумок «скільки всього лишилось».
   *
   * Три непересічні набори: за замовчуванням — те, що цех може взяти ЗАРАЗ;
   * `waiting` — робота є, але попередній цех її ще не передав;
   * `archived` — те, що цей цех уже закрив повністю.
   */
  queue: (module: ProductionModuleKey, bucket: 'active' | 'waiting' | 'archive' = 'active') =>
    apiClient
      .get<ModuleQueueRow[]>(`${BASE}modules/queue/`, {
        params: {
          module,
          archived: bucket === 'archive' ? 'true' : 'false',
          waiting: bucket === 'waiting' ? 'true' : 'false',
        },
      })
      .then((r) => r.data),

  /**
   * Зведення звітів усіх пʼяти цехів за період (обидві дати включні) —
   * живить блок «Виробнича звітність» на дашборді.
   */
  activity: (dateFrom: string, dateTo: string) =>
    apiClient
      .get<ProductionActivity>(`${BASE}activity/`, {
        params: { date_from: dateFrom, date_to: dateTo },
      })
      .then((r) => r.data),

  stats: (module: ProductionModuleKey) =>
    apiClient.get<ModuleStats>(`${BASE}modules/stats/`, { params: { module } }).then((r) => r.data),

  shiftReports: (params: ShiftReportListParams) =>
    apiClient
      .get<PaginatedResponse<ShiftReport>>(`${BASE}shift-reports/`, { params })
      .then((r) => r.data),

  /** Створює І ОДРАЗУ ПРОВОДИТЬ звіт — чернетки в цьому документі немає. */
  createShiftReport: (data: ShiftReportCreatePayload) =>
    apiClient.post<ShiftReport>(`${BASE}shift-reports/`, data).then((r) => r.data),

  /** Скасувати — «наче звіту не було» (автор ≤1 год, адмін без вікна). Відповідь 204. */
  cancelShiftReport: (id: number) =>
    apiClient.post<void>(`${BASE}shift-reports/${id}/cancel/`).then(() => undefined),

  /** Сторнувати — наслідки знімаються, документ лишається (адмін, 30 днів, з причиною). */
  reverseShiftReport: (id: number, reason: string) =>
    apiClient
      .post<ShiftReport>(`${BASE}shift-reports/${id}/reverse/`, { reason })
      .then((r) => r.data),

  scrapReports: (params: ShiftReportListParams) =>
    apiClient
      .get<PaginatedResponse<ScrapReport>>(`${BASE}scrap-reports/`, { params })
      .then((r) => r.data),

  /** Створює І ОДРАЗУ ПРОВОДИТЬ звіт про брак — чернетки немає, як і в зміні. */
  createScrapReport: (data: ScrapReportCreatePayload) =>
    apiClient.post<ScrapReport>(`${BASE}scrap-reports/`, data).then((r) => r.data),

  cancelScrapReport: (id: number) =>
    apiClient.post<void>(`${BASE}scrap-reports/${id}/cancel/`).then(() => undefined),

  reverseScrapReport: (id: number, reason: string) =>
    apiClient
      .post<ScrapReport>(`${BASE}scrap-reports/${id}/reverse/`, { reason })
      .then((r) => r.data),
};
